import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Paper,
  Chip
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const SalesDialog = ({ open, onClose, onProductAdded, onProductUpdated, initialProduct }) => {
  const { data: config } = useSelector((state) => state.config);
  const [formData, setFormData] = useState({
    name: '',
    discountPercentage: '',
    startDate: '',
    endDate: '',
    products: [],
    ...initialProduct, // Populate with initial product data if available
  });
  const [availableProducts, setAvailableProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const isEditMode = !!initialProduct?._id;

  // Fetch available products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!open) return;
      
      setLoadingProducts(true);
      try {
        const response = await apiClient.get(`${config.API_BASE_URL}/api/products`);
        const products = response.data || [];
        setAvailableProducts(products);
        setFilteredProducts(products);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [open]);

  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(availableProducts);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = availableProducts.filter(
      product => 
        (product.name && product.name.toLowerCase().includes(term)) ||
        (product._id && product._id.toLowerCase().includes(term))
    );
    setFilteredProducts(filtered);
  }, [searchTerm, availableProducts]);

  // Handle form data initialization and date formatting
  useEffect(() => {
    if (open && initialProduct) {
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date - offset).toISOString().slice(0, 16);
      };

      setFormData({
        ...initialProduct,
        startDate: formatDateForInput(initialProduct.startDate),
        endDate: formatDateForInput(initialProduct.endDate)
      });
    } else if (open && !isEditMode) {
      setFormData({
        name: '',
        discountPercentage: '',
        startDate: '',
        endDate: '',
        products: [],
      });
      setError(null);
    }
  }, [open, initialProduct, isEditMode]);

  const handleProductToggle = (product) => {
    setFormData(prev => {
      const productIds = prev.products.map(p => p._id || p);
      const newProducts = productIds.includes(product._id)
        ? prev.products.filter(p => (p._id || p) !== product._id)
        : [...prev.products, product._id];
      
      return { ...prev, products: newProducts };
    });
  };

  const isProductSelected = (product) => {
    return formData.products.some(p => (p._id || p) === product._id);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };



  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (!formData.name || !formData.discountPercentage || !formData.startDate || !formData.endDate) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    } 
    try {
      // Ensure config and API_BASE_URL are available
      if (!config || !config.API_BASE_URL) {
        setError('Configuration error: API base URL is not set.');
        setLoading(false);
        return;
      }
      const apiUrl = `${config.API_BASE_URL}/api/sales/${isEditMode ?initialProduct._id : ''}`;
      const method = isEditMode ? 'put' : 'post';

      let payload = { ...formData };
      const response = await apiClient[method](apiUrl, payload);
      setLoading(false);
      onClose(); // Close the dialog on successful operation
      if (isEditMode) {
        onProductUpdated(response.data); // Notify the parent component about the updated product
      } else {
        onProductAdded(response.data); // Notify the parent component about the new product
      }
      // Reset the form data only in add mode
      if (!isEditMode) {
        setFormData({
          name: '',
          discountPercentage: '',
          startDate: '',
          endDate: '',
          products: [],
          });
      }

    } catch (err) {
      console.error(isEditMode ? 'Error updating product:' : 'Error adding product:', err);
      setError(err.response?.data?.message || err.message || (isEditMode ? 'Failed to update product' : 'Failed to add product'));
      setLoading(false);
    }
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1400, // Higher than sidebar z-index (usually 1200-1300)
    backdropFilter: 'blur(2px)',
  };

  const modalStyle = {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    minHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1401, // Slightly higher than overlay
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  };

  const columnsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    flex: '1',
    minHeight: 0, // Allows the container to shrink below content size
  };

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  };
  
  const firstColumnStyle = {
    ...columnStyle,
    gap: '12px',
  };

  const productListStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  };

  const selectedProductsStyle = {
    marginTop: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    maxHeight: '120px',
    overflowY: 'auto',
    padding: '8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
  };

  const submitButtonStyles = {
    backgroundColor: isEditMode ? '#2196f3' : '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: isEditMode ? '#1976d2' : '#388e3c',
    },
    marginRight: '10px',
  };

  const cancelButtonStyles = {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  };

  const sectionTitleStyle = {
    marginTop: '20px',
    marginBottom: '10px',
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
    fontWeight: '600',
  };

  const imageContainerStyle = {
    position: 'relative',
    width: '100px', // Square dimension
    height: '100px', // Square dimension
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden', // Ensure image stays within bounds
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Crop image to fit container
  };

  const deleteImageIconStyle = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '50%',
    padding: '2px',
  };


  if (!open) {
    return null;
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? 'Edit Sale' : 'Add New Sale'}
        </Typography>
        {error && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>}
        
        <Box sx={columnsContainerStyle}>
          {/* First Column: Sale Details */}
          <Box sx={{ ...firstColumnStyle, pr: 2 }}>
            <Box>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                size="small"
                margin="normal"
                required
              />
              
              <TextField
                label="Discount Percentage"
                name="discountPercentage"
                type="number"
                value={formData.discountPercentage}
                onChange={handleInputChange}
                fullWidth
                size="small"
                margin="normal"
                required
                InputProps={{
                  endAdornment: '%',
                }}
              />
              
              <TextField
                label="Start Date"
                name="startDate"
                type="datetime-local"
                value={formData.startDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
                onKeyDown={(e) => e.preventDefault()}
              />
              
              <TextField
                label="End Date"
                name="endDate"
                type="datetime-local"
                value={formData.endDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
                onKeyDown={(e) => e.preventDefault()}
              />
            </Box>

            {formData.products.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Products ({formData.products.length})
                </Typography>
                <Box sx={selectedProductsStyle}>
                  {formData.products.map(productId => {
                    const product = availableProducts.find(p => p._id === productId) || 
                                  (typeof productId === 'object' ? productId : { _id: productId, name: 'Unknown Product' });
                    return (
                      <Chip
                        key={product._id}
                        label={product.name}
                        onDelete={() => handleProductToggle(product)}
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Second Column: Product Selection */}
          <Box sx={{ ...columnStyle, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">Products</Typography>
              <Typography variant="caption" color="textSecondary">
                {formData.products.length} selected
              </Typography>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name or ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <span style={{ marginRight: '8px', color: 'rgba(0, 0, 0, 0.54)' }}>
                    🔍
                  </span>
                ),
              }}
            />
            
            {loadingProducts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading products...</Typography>
              </Box>
            ) : (
              <>
                <Paper variant="outlined" sx={productListStyle}>
                  <List dense>
                    {filteredProducts.map((product) => (
                      <ListItem 
                        key={product._id}
                        button
                        onClick={() => handleProductToggle(product)}
                      >
                        <Checkbox
                          edge="start"
                          checked={isProductSelected(product)}
                          tabIndex={-1}
                          disableRipple
                        />
                        <ListItemText 
                          primary={product.name}
                          secondary={`${product.price} ${product.currency || ''}`}
                        />
                      </ListItem>
                    ))}
                    {filteredProducts.length === 0 && (
                      <ListItem>
                        <ListItemText 
                          primary="No products available"
                          primaryTypographyProps={{ color: 'textSecondary' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
                

              </>
            )}
          </Box>
        </Box>
        
        <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={buttonContainerStyle}>
            <Button
              variant="contained"
              style={submitButtonStyles}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Sale' : 'Add Sale')}
            </Button>
            <Button variant="contained" style={cancelButtonStyles} onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default SalesDialog;